<ng-container *ngIf="isAnalyzerPage; else downloaderOrDefaultTpl">
  <div class="container py-3">
    <div class="d-flex align-items-center mb-3">
      <h2 class="m-0">{{ pageTitle }}</h2>
      <button class="btn btn-link btn-sm p-0 text-primary ms-2"
              (click)="openAnalyzerHelp()"
              matTooltip="How to use RTK Base Analyzer">
        <span class="material-icons" aria-hidden="true">menu_book</span>
      </button>
    </div>

    <ng-container *ngIf="currentMountPoint as mountPoint; else analyzerEmptyState">
      <div class="mp-card">
        <div class="row g-3">
          <div class="col-12 col-lg-7">
            <div class="subsection">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="d-flex align-items-center gap-2">
                  <h4 class="subsection-title m-0">Analyze RTK Base</h4>
                  <button class="btn btn-link btn-sm p-0 text-muted"
                          matTooltip="Analyze GeoAstra NTRIP Caster mount points and generate an RTK Base report.">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </div>
                <button class="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
                        (click)="toggleQuickGuide()"
                        [attr.aria-expanded]="quickGuideExpanded">
                  <span class="d-inline-flex align-items-center">
                    <span class="material-icons me-1 text-warning" style="font-size: 18px;" aria-hidden="true">menu_book</span>
                    <span class="me-1">Quick Guide</span>
                    <span class="material-icons" style="font-size: 20px;">
                      {{ quickGuideExpanded ? 'expand_less' : 'expand_more' }}
                    </span>
                  </span>
                </button>
              </div>

              <div class="quick-start mb-3" *ngIf="quickGuideExpanded">
                <div class="quick-start-title mb-2">Steps:</div>
                <div class="quick-start-step">
                  <div class="step-left">
                    <span class="step-num">1</span>
                    <span class="step-text">Select a MountPoint (GeoAstra NTRIP Caster)</span>
                  </div>
                  <span class="badge"
                        [ngClass]="{ 'bg-success': stepMountPointStatus.tone === 'success', 'bg-secondary': stepMountPointStatus.tone === 'muted' }">
                    {{ stepMountPointStatus.label }}
                  </span>
                </div>
                <div class="quick-start-step">
                  <div class="step-left">
                    <span class="step-num">2</span>
                    <span class="step-text">Set Analysis Duration</span>
                  </div>
                  <span class="badge"
                        [ngClass]="{ 'bg-success': stepDurationStatus.tone === 'success', 'bg-secondary': stepDurationStatus.tone === 'muted' }">
                    {{ stepDurationStatus.label }}
                  </span>
                </div>
                <div class="quick-start-step">
                  <div class="step-left">
                    <span class="step-num">3</span>
                    <span class="step-text">Analyze this RTK Base</span>
                  </div>
                  <span class="badge"
                        [ngClass]="{ 'bg-success': stepAnalyzeStatus.tone === 'success', 'bg-info': stepAnalyzeStatus.tone === 'info', 'bg-secondary': stepAnalyzeStatus.tone === 'muted' }">
                    {{ stepAnalyzeStatus.label }}
                  </span>
                </div>
                <div class="quick-start-step">
                  <div class="step-left">
                    <span class="step-num">4</span>
                    <span class="step-text">Review Report</span>
                  </div>
                  <span class="badge"
                        [ngClass]="{ 'bg-success': stepReportStatus.tone === 'success', 'bg-secondary': stepReportStatus.tone === 'muted' }">
                    {{ stepReportStatus.label }}
                  </span>
                </div>
              </div>

              <div class="mb-2">
                <div class="small mb-1">Select a MountPoint:</div>
                <div class="d-flex align-items-center">
                  <div class="mp-select-wrapper" style="min-width: 280px;">
                    <select class="form-select form-select-sm mp-select"
                            [ngModel]="mountPoint.stMountPoint || ''"
                            (ngModelChange)="selectFromDropdown($event)">
                      <option value=""></option>
                      <option *ngFor="let mp of sortedList" [value]="mp.stMountPoint">{{ mp.stMountPoint }}</option>
                    </select>
                  </div>
                  <span class="ms-2"
                        [title]="isMountpointActive(mountPoint.stMountPoint) ? 'Online' : 'Offline'">
                    (
                    <span [ngStyle]="{ display: 'inline-block', width: '10px', height: '10px', 'border-radius': '50%', 'background-color': isMountpointActive(mountPoint.stMountPoint) ? '#22c55e' : '#9ca3af' }"></span>
                    <span class="ms-1">{{ isMountpointActive(mountPoint.stMountPoint) ? 'Online' : 'Offline' }}</span>
                    )
                  </span>
                </div>
              </div>

              <div class="row g-2">
                <div class="col-md-5">
                  <label class="form-label small d-inline-flex align-items-center">
                    <span>Analysis Duration</span>
                    <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                            matTooltip="How long to analyze RTCM data from this GeoAstra mount point.">
                      <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                    </button>
                  </label>
                  <select class="form-select form-select-sm"
                          style="max-width: 220px;"
                          [ngModel]="analyzerDurationByMountpoint[mountPoint.stMountPoint] || 120"
                          (ngModelChange)="setAnalyzerDuration(mountPoint.stMountPoint, $event)">
                    <option [ngValue]="10">10 seconds</option>
                    <option [ngValue]="30">30 seconds</option>
                    <option [ngValue]="60">60 seconds</option>
                    <option [ngValue]="120">120 seconds</option>
                  </select>
                </div>
              </div>

              <div class="d-flex align-items-center mt-3">
                <button type="button"
                        class="btn btn-sm btn-primary"
                        [disabled]="!isMountpointActive(mountPoint.stMountPoint)"
                        (click)="onStartAnalysis(mountPoint)"
                        matTooltip="Starts an analyzer task in backend. You can leave this page and come back later.">
                  <span class="d-inline-flex align-items-center">
                    <span class="material-icons me-1" style="font-size: 1.1rem;">troubleshoot</span>
                    <span>{{ getAnalyzeButtonLabel(mountPoint) }}</span>
                  </span>
                </button>
              </div>

              <!-- Countdown progress (kept) -->
              <div class="mt-2" *ngIf="analysisCountdownActive && mountPoint.stMountPoint === analysisCountdownMountpoint">
                <div class="analysis-progress">
                  <div class="analysis-header d-flex justify-content-between align-items-center">
                    <div class="title">{{ (warmupRemaining > 0) ? 'Analysis server starting ...' : 'Pulling Data from the RTK Base in progress …' }}</div>
                    <div class="time small text-muted">
                      Elapsed {{ getAnalysisElapsedLabel() }} · Remaining {{ getAnalysisRemainingLabel() }}
                    </div>
                  </div>
                  <div class="bar-wrap">
                    <div class="bar-bg">
                      <ng-container *ngIf="getAnalysisPercent() === 0 && analysisCountdownActive && warmupRemaining > 0; else progressBarFill">
                        <div class="bar-fill" [style.width]="'0%'"><span class="bar-label">Analysis server starting ...</span></div>
                      </ng-container>
                      <ng-template #progressBarFill>
                        <div class="bar-fill" [style.width]="getAnalysisPercent() + '%'">
                          <span class="bar-label">{{ getAnalysisPercent() < 20 ? (getAnalysisPercent() + '%') : (getAnalysisPercent() + '% completed') }}</span>
                        </div>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Map section (if enabled by inputs) -->
            <div class="row mt-3 subsection" *ngIf="showMap && hasAnalyzerSummary(mountPoint.stMountPoint)">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                  <h4 class="m-0 subsection-title">RTK Base Station Location</h4>
                  <div class="ms-auto d-flex align-items-center gap-3">
                    <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn"
                            (click)="toggleMapSection(mountPoint.stMountPoint)"
                            [attr.aria-expanded]="!isMapCollapsed(mountPoint.stMountPoint)"
                            aria-label="Toggle map">
                      <span class="material-icons" *ngIf="isMapCollapsed(mountPoint.stMountPoint)">chevron_right</span>
                      <span class="material-icons" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">expand_more</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint) && analyzerSummaryByMountpoint[mountPoint.stMountPoint] as smry">
                <div class="mp-meta mb-2">
                  <div>RTK Base Coordinate: <span class="mp-value">{{ smry['location'] }}</span></div>
                  <div>RTK base Geodetic Height (meter): <span class="mp-value">{{ smry['geodetic_height_meters'] }}</span></div>
                  <div>RTK base Orthometric Height (meter): <span class="mp-value">{{ smry['orthometric_height_meters'] }}</span></div>
                </div>
              </div>
              <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">
                <div class="map-box" [id]="'map_' + mountPoint.stMountPoint"></div>
              </div>
              <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">
                <div class="ext-map-grid w-100" style="grid-template-columns: repeat(3, 1fr);">
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(mountPoint.stMountPoint, 'sat')">Google Satellite</a>
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(mountPoint.stMountPoint, 'map')">Google Street</a>
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(mountPoint.stMountPoint, 'sat')">Bing Satellite</a>
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(mountPoint.stMountPoint, 'map')">Bing Street</a>
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getAmapUrl(mountPoint.stMountPoint, 'map')">Amap Street & Satellite</a>
                  <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBaiduUrl(mountPoint.stMountPoint, 'map')">Baidu Street & Satellite</a>
                </div>
              </div>
            </div>

            <div class="analysis-result mt-3">
              <div class="file-card">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="subsection-title m-0 d-inline-flex align-items-center">
                    <span>Analysis Result</span>
                    <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                            matTooltip="The report will load when ready. Use Expand/Collapse to browse JSON.">
                      <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                    </button>
                  </div>
                </div>

                <div class="d-flex gap-2 mb-2" *ngIf="analysisJsonByMountpoint[mountPoint.stMountPoint]">
                  <button class="btn btn-sm btn-outline-secondary" (click)="expandAll(mountPoint.stMountPoint)">Expand All</button>
                  <button class="btn btn-sm btn-outline-secondary" (click)="collapseAll(mountPoint.stMountPoint)">Collapse All</button>
                  <button class="btn btn-sm btn-outline-secondary" (click)="copyJson(mountPoint.stMountPoint)">Copy JSON</button>
                </div>

                <ng-container *ngIf="analysisJsonByMountpoint[mountPoint.stMountPoint] as result; else noAnalysisResult">
                  <div class="json-tree">
                    <ng-template #renderNode let-key="key" let-value="value" let-path="path" let-mp="mp">
                      <div class="json-row" *ngIf="!isObject(value) && !isArray(value)">
                        <span class="json-key">{{ key }}:</span>
                        <a *ngIf="isUrl(value)" class="json-value" [href]="value" target="_blank" rel="noopener">{{ value }}</a>
                        <span *ngIf="!isUrl(value)" class="json-value">{{ value }}</span>
                      </div>
                      <div class="json-node" *ngIf="isObject(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <div class="json-children" *ngIf="isExpanded(mp, path)">
                          <ng-container *ngFor="let child of value | keyvalue:keyOrder">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: child.key, value: child.value, path: path + '.' + child.key, mp: mp }"></ng-container>
                          </ng-container>
                        </div>
                      </div>
                      <div class="json-array" *ngIf="isArray(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <ul class="json-ul" *ngIf="isExpanded(mp, path)">
                          <li *ngFor="let v of value; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: path + '[' + idx + ']', mp: mp }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>

                    <ng-container *ngIf="isObject(result); else renderRootArray">
                      <ng-container *ngFor="let item of result | keyvalue:keyOrder">
                        <ng-container *ngTemplateOutlet="renderNode; context: { key: item.key, value: item.value, path: item.key, mp: mountPoint.stMountPoint }"></ng-container>
                      </ng-container>
                    </ng-container>
                    <ng-template #renderRootArray>
                      <div class="json-array">
                        <ul class="json-ul">
                          <li *ngFor="let v of result; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: '[' + idx + ']', mp: mountPoint.stMountPoint }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>
                  </div>
                </ng-container>

                <ng-template #noAnalysisResult>
                  <div class="text-muted small">
                    No analysis report available yet. Click Analyze this RTK Base, then use Refresh to check status and load the report.
                  </div>
                </ng-template>
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-5 d-flex align-items-start">
            <img src="assets/images/landing/GeoAstra_RTK_Base_Analyzer_diagram.drawio.png"
                 alt="RTK Base Analyzer Diagram"
                 class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
          </div>
        </div>
      </div>
    </ng-container>

    <ng-template #analyzerEmptyState>
      <div class="text-muted small">No mount points available.</div>
    </ng-template>
  </div>
</ng-container>

<ng-template #downloaderOrDefaultTpl>
  <ng-container *ngIf="isDownloaderPage; else defaultTpl">
    <div class="container py-3">
      <div class="d-flex align-items-center mb-3">
        <h2 class="m-0">{{ pageTitle }}</h2>
        <button class="btn btn-link btn-sm p-0 text-primary ms-2"
                (click)="openDownloaderHelp()"
                matTooltip="How to use PPK Downloader">
          <span class="material-icons" aria-hidden="true">menu_book</span>
        </button>
      </div>

      <ng-container *ngIf="currentMountPoint as mountPoint; else downloaderEmptyState">
        <div class="mp-card">
          <div class="row g-3">
            <div class="col-12 col-lg-7">
              <div class="subsection">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="d-flex align-items-center gap-2">
                    <h4 class="subsection-title m-0">Record MountPoint Data</h4>
                    <button class="btn btn-link btn-sm p-0 text-muted"
                            matTooltip="Schedule a backend task to download RTCM data from the selected GeoAstra NTRIP Caster mount point.">
                      <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                    </button>
                  </div>
                  <button class="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
                          (click)="toggleQuickGuide()"
                          [attr.aria-expanded]="quickGuideExpanded">
                    <span class="d-inline-flex align-items-center">
                      <span class="material-icons me-1 text-warning" style="font-size: 18px;" aria-hidden="true">menu_book</span>
                      <span class="me-1">Quick Guide</span>
                      <span class="material-icons" style="font-size: 20px;">
                        {{ quickGuideExpanded ? 'expand_less' : 'expand_more' }}
                      </span>
                    </span>
                  </button>
                </div>

                <div class="quick-start mb-3" *ngIf="quickGuideExpanded">
                  <div class="quick-start-title mb-2">Steps:</div>
                  <div class="quick-start-step">
                    <div class="step-left">
                      <span class="step-num">1</span>
                      <span class="step-text">Select a MountPoint (GeoAstra NTRIP Caster)</span>
                    </div>
                    <span class="badge"
                          [ngClass]="{ 'bg-success': dlStepMountPointStatus.tone === 'success', 'bg-secondary': dlStepMountPointStatus.tone === 'muted' }">
                      {{ dlStepMountPointStatus.label }}
                    </span>
                  </div>
                  <div class="quick-start-step">
                    <div class="step-left">
                      <span class="step-num">2</span>
                      <span class="step-text">Set Recording Start Time</span>
                    </div>
                    <span class="badge"
                          [ngClass]="{ 'bg-success': dlStepStartTimeStatus.tone === 'success', 'bg-secondary': dlStepStartTimeStatus.tone === 'muted' }">
                      {{ dlStepStartTimeStatus.label }}
                    </span>
                  </div>
                  <div class="quick-start-step">
                    <div class="step-left">
                      <span class="step-num">3</span>
                      <span class="step-text">Set Recording Duration</span>
                    </div>
                    <span class="badge"
                          [ngClass]="{ 'bg-success': dlStepDurationStatus.tone === 'success', 'bg-secondary': dlStepDurationStatus.tone === 'muted' }">
                      {{ dlStepDurationStatus.label }}
                    </span>
                  </div>
                  <div class="quick-start-step">
                    <div class="step-left">
                      <span class="step-num">4</span>
                      <span class="step-text">Submit the Task</span>
                    </div>
                    <span class="badge"
                          [ngClass]="{ 'bg-success': dlStepSubmitStatus.tone === 'success', 'bg-info': dlStepSubmitStatus.tone === 'info', 'bg-secondary': dlStepSubmitStatus.tone === 'muted' }">
                      {{ dlStepSubmitStatus.label }}
                    </span>
                  </div>
                  <div class="quick-start-step">
                    <div class="step-left">
                      <span class="step-num">5</span>
                      <span class="step-text">Downloaded File</span>
                    </div>
                    <span class="badge"
                          [ngClass]="{ 'bg-success': dlStepDownloadStatus.tone === 'success', 'bg-danger': dlStepDownloadStatus.tone === 'danger', 'bg-secondary': dlStepDownloadStatus.tone === 'muted' }">
                      {{ dlStepDownloadStatus.label }}
                    </span>
                  </div>
                </div>

                <div class="mb-2">
                  <div class="small mb-1">Select a MountPoint:</div>
                  <div class="d-flex align-items-center">
                    <div class="mp-select-wrapper" style="min-width: 280px;">
                      <select class="form-select form-select-sm mp-select"
                              [ngModel]="mountPoint.stMountPoint || ''"
                              (ngModelChange)="selectFromDropdown($event)">
                        <option value=""></option>
                        <option *ngFor="let mp of sortedList" [value]="mp.stMountPoint">{{ mp.stMountPoint }}</option>
                      </select>
                    </div>
                    <span class="ms-2"
                          [title]="isMountpointActive(mountPoint.stMountPoint) ? 'Online' : 'Offline'">
                      (
                      <span [ngStyle]="{ display: 'inline-block', width: '10px', height: '10px', 'border-radius': '50%', 'background-color': isMountpointActive(mountPoint.stMountPoint) ? '#22c55e' : '#9ca3af' }"></span>
                      <span class="ms-1">{{ isMountpointActive(mountPoint.stMountPoint) ? 'Online' : 'Offline' }}</span>
                      )
                    </span>
                  </div>
                </div>

                <div class="row g-2">
                  <div class="col-md-5">
                    <label class="form-label small d-inline-flex align-items-center">
                      <span>Recording Start Time</span>
                      <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                              type="button"
                              (click)="openDownloaderStartTimeHelp()"
                              matTooltip="Recording Start Time rules"
                              aria-label="Recording Start Time rules">
                        <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                      </button>
                    </label>
                    <input type="datetime-local" class="form-control form-control-sm"
                           [ngModel]="getPlanStartAtInput(mountPoint.stMountPoint)"
                           (ngModelChange)="onPlanStartAtInputChange(mountPoint.stMountPoint, $event)"
                           (keydown)="$event.preventDefault()" (keypress)="$event.preventDefault()" (paste)="$event.preventDefault()"
                           [attr.inputmode]="'none'">
                  </div>
                  <div class="col-md-4">
                    <label class="form-label small d-inline-flex align-items-center">
                      <span>Recording Duration</span>
                      <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                              matTooltip="How long to record RTCM correction data.">
                        <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                      </button>
                    </label>
                    <select class="form-select form-select-sm"
                            [ngModel]="getDurationSelection(mountPoint.stMountPoint)"
                            (ngModelChange)="onDurationSelectionChange(mountPoint.stMountPoint, $event)">
                      <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                    </select>
                  </div>
                </div>

                <div class="d-flex align-items-center mt-3">
                  <button type="button"
                          class="btn btn-sm btn-primary"
                          (click)="onStartDownloader(mountPoint)"
                          [disabled]="downloaderLoading[mountPoint.stMountPoint] || (downloaderUrlByMountpoint[mountPoint.stMountPoint]?.running) || isDownloaderStartTimeRejected(mountPoint.stMountPoint) || !isDownloaderStartTimeValid(mountPoint.stMountPoint)"
                          matTooltip="Schedules the recording. You can leave this page; the task runs in backend.">
                    <span class="d-inline-flex align-items-center">
                      <span class="material-icons me-1" style="font-size: 1.1rem;">send</span>
                      <span>Submit the Task</span>
                    </span>
                  </button>
                </div>
              </div>

              <div class="record-card mt-3">
                <div class="file-card">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="subsection-title m-0 d-inline-flex align-items-center">
                      <span>Downloaded File:</span>
                      <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                              matTooltip="Click Refresh to check status and get the download link when ready.">
                        <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                      </button>
                    </div>
                    <button class="btn btn-sm btn-outline-primary"
                            type="button"
                            (click)="onGetDownloaderUrl(mountPoint)"
                            matTooltip="Refresh status"
                            aria-label="Refresh status">
                      <span class="d-inline-flex align-items-center">
                        <span class="material-icons me-1" style="font-size: 1.1rem;" aria-hidden="true">refresh</span>
                        <span>Refresh</span>
                      </span>
                    </button>
                  </div>
                  <div class="file-card-row">
                    <ng-container *ngIf="downloaderUrlByMountpoint[mountPoint.stMountPoint] as dl; else dlEmpty">
                      <ng-container *ngIf="!dl?.running; else runningTpl">
                        <ng-container *ngIf="dl?.url; else noFileTpl">
                          <div class="w-100 me-3">
                            <div class="d-flex align-items-center justify-content-between gap-3">
                              <a class="fw-semibold link-primary"
                                 [href]="isExpired(dl) ? null : dl.url"
                                 [class.disabled]="isExpired(dl)"
                                 [attr.aria-disabled]="isExpired(dl) ? true : null"
                                 (click)="isExpired(dl) ? $event.preventDefault() : null"
                                 target="_blank" rel="noopener">
                                rtcm_data.rtcm3
                              </a>
                              <div class="text-muted fw-semibold flex-shrink-0">
                                {{ dl.lastModified ? (dl.lastModified | date:'medium') : '--' }}
                              </div>
                            </div>
                            <div *ngIf="isExpired(dl)" class="text-primary small mt-1">
                              (This file has expired and is no longer available for download.)
                            </div>
                          </div>
                        </ng-container>
                        <ng-template #noFileTpl>
                          <div class="text-muted">No downloaded file yet. Submit a task above, then click Refresh to check status.</div>
                        </ng-template>
                      </ng-container>
                      <ng-template #runningTpl>
                        <div class="text-primary small">A recorder is currently running or scheduled. Please check back later.</div>
                      </ng-template>
                    </ng-container>
                    <ng-template #dlEmpty>
                      <div class="text-muted">No downloaded file yet. Submit a task above, then click Refresh to check status.</div>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-5 d-flex align-items-start">
              <img src="assets/images/landing/GeoAstra_PPK_Downloader_diagram.drawio.png"
                   alt="PPK Downloader Diagram"
                   class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #downloaderEmptyState>
        <div class="text-muted small">No mount points available.</div>
      </ng-template>
    </div>
  </ng-container>

  <ng-template #defaultTpl>

<div class="container py-3">
  <h2 class="mb-3">{{ pageTitle }}</h2>

    <div >
      <!-- 顶部区域：Mountpoints 列表 -->
      
      <div class="col-12">     
          <!-- 分页导航（移动到标题行上方） -->
          <div class="d-flex justify-content-between align-items-center mt-3 mb-2" *ngIf="showPaginationNav">
            <div class="d-flex align-items-center">
              <div><strong>MountPoint:</strong> {{ currentPage }} / {{ totalPages || 1 }}</div>
            </div>
            <div class="btn-toolbar" role="toolbar" aria-label="pagination">
              <div class="btn-group me-2" role="group" aria-label="first-prev">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goFirst()" [disabled]="currentPage === 1">« First</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goPrev()" [disabled]="currentPage === 1">‹ Prev</button>
              </div>
              <div class="btn-group me-2" role="group" aria-label="pages">
                <button *ngFor="let p of pageWindow" type="button"
                        class="btn btn-sm"
                        [ngClass]="{'btn-primary': p === currentPage, 'btn-outline-secondary': p !== currentPage}"
                        (click)="goToPage(p)">{{ p }}</button>
              </div>
              <div class="btn-group" role="group" aria-label="next-last">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goNext()" [disabled]="currentPage === totalPages">Next ›</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="goLast()" [disabled]="currentPage === totalPages">Last »</button>
              </div>
            </div>
          </div>
          <!-- New row: MountPoint dropdown + Add/Delete buttons -->
          <div class="mt-2 mb-2" *ngIf="sortedList.length > 0 || showAddDelButtons">
            <div class="small mb-1">Select a MountPoint:</div>
            <div class="d-flex align-items-center flex-wrap gap-2">
              <div class="mp-select-wrapper" style="min-width: 280px;">
                <select class="form-select form-select-sm mp-select"
                        [disabled]="sortedList.length === 0"
                        [ngModel]="currentMountPoint?.stMountPoint || ''"
                        (ngModelChange)="selectFromDropdown($event)">
                  <option value=""></option>
                  <option *ngFor="let mp of sortedList" [value]="mp.stMountPoint">{{ mp.stMountPoint }}</option>
                </select>
              </div>
              <ng-container *ngIf="currentMountPoint as curMp">
                <span class="ms-2" [title]="isMountpointActive(curMp.stMountPoint) ? 'Online' : 'Offline'">
                  (
                  <span [ngStyle]="{ display: 'inline-block', width: '10px', height: '10px', 'border-radius': '50%', 'background-color': isMountpointActive(curMp.stMountPoint) ? '#22c55e' : '#9ca3af' }"></span>
                  <span class="ms-1">{{ isMountpointActive(curMp.stMountPoint) ? 'Online' : 'Offline' }}</span>
                  )
                </span>
              </ng-container>
              <ng-container *ngIf="showAddDelButtons">
                <button class="btn btn-sm btn-danger ms-3"
                        type="button"
                        [disabled]="!currentMountPoint"
                        (click)="currentMountPoint && openConfirmDialog(currentMountPoint)">
                  Del this MountPoint
                </button>
                <button class="btn btn-sm btn-primary ms-2"
                        type="button"
                        (click)="generateNewMountPointForUser()">
                  Add a new MountPoint
                </button>
              </ng-container>
            </div>

            <div class="text-muted small mt-2" *ngIf="sortedList.length === 0">
              No mount points available. Click <strong>Add a new MountPoint</strong> to create one.
            </div>
          </div>
          <!-- New row: MountPoint Data from + refresh button on right (hidden on PPK Downloader page) -->
          <ng-container *ngIf="!showDownloader">
            <div class="col-12 mt-2" *ngIf="currentMountPoint as curMp2">
              <div class="d-flex align-items-center">
                <button *ngIf="showGetInfoButton" type="button" class="btn btn-sm btn-primary me-3"
                        [disabled]="!isMountpointActive(curMp2.stMountPoint) || analysisCountdownActive"
                        (click)="onStartAnalysis(curMp2)">
                  <span class="material-icons me-1 align-middle">download</span>
                  Get RTK Base Location
                </button>
                <ng-container *ngIf="generatedAtByMountpoint[curMp2.stMountPoint] as lastAt; else noResultMsg">
                  <div class="analysis-inline-text">(Last Data refresh: <span class="mp-value">{{ lastAt }}</span>)</div>
                </ng-container>
                <ng-template #noResultMsg>
                  <button *ngIf="!isMountpointActive(curMp2.stMountPoint)"
                          type="button"
                          class="btn btn-link btn-sm p-0 text-muted"
                          matTooltip="To enable this button, please connect your RTK Base to this mountpoint."
                          aria-label="How to enable Get RTK Base Location">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </ng-template>
              </div>
            </div>
          </ng-container>
          <!-- 150秒倒计时进度条：仅在当前挂载点即为正在刷新者时显示（专业厚进度条） -->
          <div class="col-12 mt-2" *ngIf="analysisCountdownActive && currentMountPoint?.stMountPoint === analysisCountdownMountpoint">
            <div class="analysis-progress">
              <div class="analysis-header d-flex justify-content-between align-items-center">
                <div class="title">{{ (warmupRemaining > 0) ? 'Analysis server starting ...' : 'Pulling Data from the RTK Base in progress …' }}</div>
                <div class="time small text-muted">
                  Elapsed {{ getAnalysisElapsedLabel() }} · Remaining {{ getAnalysisRemainingLabel() }}
                </div>
              </div>
              <div class="bar-wrap">
                <div class="bar-bg">
                  <ng-container *ngIf="getAnalysisPercent() === 0 && analysisCountdownActive && warmupRemaining > 0; else progressBarFill">
                    <div class="bar-fill" [style.width]="'0%'"><span class="bar-label">Analysis server starting ...</span></div>
                  </ng-container>
                  <ng-template #progressBarFill>
                    <div class="bar-fill" [style.width]="getAnalysisPercent() + '%'">
                      <span class="bar-label">{{ getAnalysisPercent() < 20 ? (getAnalysisPercent() + '%') : (getAnalysisPercent() + '% completed') }}</span>
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>
          <!-- Divider below progress bar -->
          <div class="col-12 mt-2">
            <div class="divider"></div>
          </div>
          <!-- 标题行已去除 -->

          <!-- 单项（每页 1 条） -->
          <div class="list-group mt-2">
            <div *ngIf="currentMountPoint as mountPoint" class="list-group-item">
              <!-- 第一行：左侧名称 + 右侧按钮 -->
              <div class="d-flex justify-content-between align-items-center mb-2 subsection subheaderless" style="min-height: 10px;">
                <!-- Intentionally left blank as requested -->
                <div class="w-100"></div>
              </div>
              <!-- 第二行：基础信息（与主区域合并，无子分区外框/标题） -->
              <div class="mp-meta">
                <ng-container *ngIf="introOverride; else analyzerMsgs">
                  <div class="row g-3 align-items-start mb-2">
                    <div class="col-12 col-md-7">
                      <div class="alert py-2 px-3 h-100" [ngClass]="{'alert-info': introOverrideVariant==='info', 'alert-warning': introOverrideVariant==='warning'}">
                        <ng-container *ngFor="let para of introOverride.split('\n')">
                          <p class="intro-paragraph mb-2">{{ para }}</p>
                        </ng-container>
                      </div>
                      <!-- Place the PPK Downloader subsection directly under the message box (left column) on PPK page -->
                      <ng-container *ngTemplateOutlet="downloaderSection"></ng-container>
                    </div>
                    <div class="col-12 col-md-5 d-flex align-items-start">
                      <img src="assets/images/landing/GeoAstra_PPK_Downloader_diagram.drawio.png" alt="PPK Downloader Diagram" class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
                    </div>
                  </div>
                </ng-container>
                <ng-template #analyzerMsgs>
                  <ng-container *ngIf="isAnalyzerPage; else defaultAnalyzerMsg">
                    <ng-container *ngIf="analyzerSummaryByMountpoint[mountPoint.stMountPoint] as smry; else analyzerRecommend">
                      <div class="row g-3 align-items-start mb-2">
                        <div class="col-12 col-md-7">
                          <div class="alert alert-info py-2 px-3 h-100">
                            The RTK Base data shown below was collected on
                            <strong>&lt;{{ generatedAtByMountpoint[mountPoint.stMountPoint] || smry['generated_at'] }}&gt;</strong>
                            and is not real-time. Click the <strong>Re-analyze this RTK Base</strong> button to refresh the data.
                          </div>
                          <!-- Place the RTK Base Analysis Report subsection directly under the message box (left column) on analyzer page -->
                          <ng-container *ngTemplateOutlet="analysisSection"></ng-container>
                        </div>
                        <div class="col-12 col-md-5 d-flex align-items-start">
                          <img src="assets/images/landing/GeoAstra_RTK_Base_Analyzer_diagram.drawio.png" alt="RTK Base Analyzer Diagram" class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
                        </div>
                      </div>
                    </ng-container>
                    <ng-template #analyzerRecommend>
                      <div class="row g-3 align-items-start mb-2">
                        <div class="col-12 col-md-7">
                          <div class="alert alert-warning py-2 px-3 h-100">
                            To view the RTK Base Analysis report, first connect your RTK Base to this Mountpoint and click the <strong>Analyze this RTK Base</strong> button below. This is a one-time process and usually takes less than 2-3 minutes to complete.
                          </div>
                          <!-- Place the RTK Base Analysis Report subsection directly under the message box (left column) on analyzer page when no result.json -->
                          <ng-container *ngTemplateOutlet="analysisSection"></ng-container>
                        </div>
                        <div class="col-12 col-md-5 d-flex align-items-start">
                          <img src="assets/images/landing/GeoAstra_RTK_Base_Analyzer_diagram.drawio.png" alt="RTK Base Analyzer Diagram" class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
                        </div>
                      </div>
                    </ng-template>
                  </ng-container>
                  <ng-template #defaultAnalyzerMsg>
                  <ng-container *ngIf="analyzerSummaryByMountpoint[mountPoint.stMountPoint] as smry; else recommendBox">
                    <!-- message box removed per requirement -->
                  </ng-container>
                  <ng-template #recommendBox>
                    <!-- message box removed per requirement -->
                  </ng-template>
                  </ng-template>
                </ng-template>
                
                <!-- MountPoint Details subsection (title always visible; body collapsible) -->
                <ng-template #detailsSection>
                <div class="row mt-2 subsection" *ngIf="showDetails">
                  <div class="col-12">
                    <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                      <h4 class="m-0 subsection-title">MountPoint Details</h4>
                      <div class="ms-auto d-flex align-items-center gap-3">
                        <div class="form-check form-switch m-0" *ngIf="!isDetailsCollapsed(mountPoint.stMountPoint)">
                          <input class="form-check-input" type="checkbox" [id]="'editSwitch_' + mountPoint.stMountPoint" [checked]="canEdit" (change)="canEdit = $any($event.target).checked; action = canEdit ? 'edit' : 'show'">
                          <label class="form-check-label small ms-2" [attr.for]="'editSwitch_' + mountPoint.stMountPoint">Editing</label>
                        </div>
                        <button type="button" class="btn btn-sm btn-primary" *ngIf="!isDetailsCollapsed(mountPoint.stMountPoint) && canEdit" (click)="onSubmit()">Update</button>
                        <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn" (click)="toggleDetailsSection(mountPoint)" [attr.aria-expanded]="!isDetailsCollapsed(mountPoint.stMountPoint)" aria-label="Toggle details">
                          <span class="material-icons" *ngIf="isDetailsCollapsed(mountPoint.stMountPoint)">chevron_right</span>
                          <span class="material-icons" *ngIf="!isDetailsCollapsed(mountPoint.stMountPoint)">expand_more</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-12" *ngIf="!isDetailsCollapsed(mountPoint.stMountPoint) && selectedMountPoint === mountPoint">
                    <form (ngSubmit)="onSubmit()" class="row g-2 form-edit ">
                      <hr>
                      <div class="row g-2">
                        <div class="col-12">

                          <div class="row row-cols-1 row-cols-md-4 row-cols-lg-4 g-3 mb-3">
                            <div class="col">
                              <label for="stMountPoint" class="form-label d-flex align-items-center">MountPoint
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['mountpoint'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'"  class="form-control" id="stMountPoint" [(ngModel)]="selectedMountPoint.stMountPoint" name="stMountPoint" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stIdentifier" class="form-label d-flex align-items-center">Identifier
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['identifier'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stIdentifier" [(ngModel)]="selectedMountPoint.stIdentifier" name="stIdentifier" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stFormat" class="form-label d-flex align-items-center">Format
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['format'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stFormat" [(ngModel)]="selectedMountPoint.stFormat" name="stFormat" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stCarrier" class="form-label d-flex align-items-center">Carrier
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['carrier'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stCarrier" [(ngModel)]="selectedMountPoint.stCarrier" name="stCarrier" [disabled]="!canEdit">
                            </div>
                          </div>

                          <div class="row row-cols-1 row-cols-md-4 g-3 mb-3">
                            <div class="col">
                              <label for="stFormatDetails" class="form-label d-flex align-items-center">Format Details
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['formatDetails'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stFormatDetails" [(ngModel)]="selectedMountPoint.stFormatDetails" name="stFormatDetails" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stNavSystem" class="form-label d-flex align-items-center">Nav System
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['navSystem'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stNavSystem" [(ngModel)]="selectedMountPoint.stNavSystem" name="stNavSystem" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stNetwork" class="form-label d-flex align-items-center">Network
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['network'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stNetwork" [(ngModel)]="selectedMountPoint.stNetwork" name="stNetwork" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stCountry" class="form-label d-flex align-items-center">Country
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['country'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stCountry" [(ngModel)]="selectedMountPoint.stCountry" name="stCountry" [disabled]="!canEdit">
                            </div>
                          </div>

                          <div class="row row-cols-1 row-cols-md-4 g-3 mb-3">
                            <div class="col">
                              <label for="stLatitude" class="form-label d-flex align-items-center">Latitude
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['latitude'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stLatitude" [(ngModel)]="selectedMountPoint.stLatitude" name="stLatitude" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stLongitude" class="form-label d-flex align-items-center">Longitude
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['longitude'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stLongitude" [(ngModel)]="selectedMountPoint.stLongitude" name="stLongitude" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stNmea" class="form-label d-flex align-items-center">NMEA
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['nmea'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stNmea" [(ngModel)]="selectedMountPoint.stNmea" name="stNmea" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stSolution" class="form-label d-flex align-items-center">Solution
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['solution'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stSolution" [(ngModel)]="selectedMountPoint.stSolution" name="stSolution" [disabled]="!canEdit">
                            </div>
                          </div>

                          <div class="row row-cols-1 row-cols-md-4 g-3 mb-3">
                            <div class="col">
                              <label for="stGenerator" class="form-label d-flex align-items-center">Generator
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['generator'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stGenerator" [(ngModel)]="selectedMountPoint.stGenerator" name="stGenerator" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stCompression" class="form-label d-flex align-items-center">Compression
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['compression'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stCompression" [(ngModel)]="selectedMountPoint.stCompression" name="stCompression" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stAuthentication" class="form-label d-flex align-items-center">Authentication
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['authentication'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stAuthentication" [(ngModel)]="selectedMountPoint.stAuthentication" name="stAuthentication" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stFee" class="form-label d-flex align-items-center">Fee
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['fee'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stFee" [(ngModel)]="selectedMountPoint.stFee" name="stFee" [disabled]="!canEdit">
                            </div>
                          </div>

                          <div class="row row-cols-1 row-cols-md-4 row-cols-lg-4 g-3 mb-3">
                            <div class="col">
                              <label for="stBitrate" class="form-label d-flex align-items-center">Bitrate
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['bitrate'].description}}">info</span>
                              </label>
                              <input type="number" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stBitrate" [(ngModel)]="selectedMountPoint.stBitrate" name="stBitrate" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label for="stMisc" class="form-label d-flex align-items-center">Misc
                                <span class="material-icons ms-1 text-muted" matTooltip="{{helpData['misc'].description}}">info</span>
                              </label>
                              <input type="text" [style.color]="canEdit ? 'blue' : 'inherit'" class="form-control" id="stMisc" [(ngModel)]="selectedMountPoint.stMisc" name="stMisc" [disabled]="!canEdit">
                            </div>
                            <div class="col">
                              <label class="form-label d-flex align-items-center">Is Active?
                                <span class="material-icons ms-1 text-muted" matTooltip="MountPoint active status">info</span>
                              </label>
                              <div class="form-check mt-1">
                                <input class="form-check-input" type="checkbox" [(ngModel)]="selectedMountPoint.active" name="active" [disabled]="!canEdit">
                              </div>
                            </div>
                            <div class="col"></div>
                          </div>

                          <br>

                          <!-- 移除底部 Update，仅保留顶部 Update -->
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                </ng-template>

                <!-- 地图与统计（Analyzer 概要加载后展示） -->
                <ng-container *ngIf="showMap && analyzerSummaryByMountpoint[mountPoint.stMountPoint] as smry">
                  <!-- 地图：放在基本信息下方，留出空隙 -->
                  <div class="mt-3"></div>
                  <!-- Render in desired order: Map only (SNR removed) -->
                  <ng-container *ngTemplateOutlet="mapBlock"></ng-container>
                </ng-container>
                <!-- Always render MountPoint Details, regardless of analysis result availability -->
                <ng-container *ngTemplateOutlet="detailsSection"></ng-container>

                <!-- Compose SNR template removed -->

                <!-- Compose Map template -->
                <ng-template #mapBlock>
                  <div class="row mt-2 subsection" *ngIf="hasAnalyzerSummary(mountPoint.stMountPoint)">
                    <div class="col-12">
                      <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                        <ng-container *ngIf="analyzerSummaryByMountpoint[mountPoint.stMountPoint]; else rtkTitleFallback">
                          <h4 class="m-0 subsection-title">RTK Base Station Location</h4>
                        </ng-container>
                        <div class="ms-auto d-flex align-items-center gap-3">
                          <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn"
                                  (click)="toggleMapSection(mountPoint.stMountPoint)"
                                  [attr.aria-expanded]="!isMapCollapsed(mountPoint.stMountPoint)"
                                  aria-label="Toggle map">
                            <span class="material-icons" *ngIf="isMapCollapsed(mountPoint.stMountPoint)">chevron_right</span>
                            <span class="material-icons" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">expand_more</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <ng-template #rtkTitleFallback>
                      <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                          <h4 class="m-0 subsection-title">RTK Base Station Location</h4>
                        </div>
                      </div>
                    </ng-template>
                    <!-- Body titles moved from subtitle -->
                    <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">
                      <ng-container *ngIf="analyzerSummaryByMountpoint[mountPoint.stMountPoint] as smry">
                        <div class="mp-meta mb-2">
                          <div>RTK Base Coordinate: <span class="mp-value">{{ smry['location'] }}</span></div>
                          <div>RTK base Geodetic Height (meter): <span class="mp-value">{{ smry['geodetic_height_meters'] }}</span></div>
                          <div>RTK base Orthometric Height (meter): <span class="mp-value">{{ smry['orthometric_height_meters'] }}</span></div>
                        </div>
                      </ng-container>
                    </div>
                    <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">
                      <div class="map-box" [id]="'map_' + mountPoint.stMountPoint"></div>
                    </div>
                    <div class="col-12" *ngIf="!isMapCollapsed(mountPoint.stMountPoint)">
                      <div class="ext-map-grid w-100" style="grid-template-columns: repeat(3, 1fr);">
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(mountPoint.stMountPoint, 'sat')">Google Satellite</a>
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(mountPoint.stMountPoint, 'map')">Google Street</a>
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(mountPoint.stMountPoint, 'sat')">Bing Satellite</a>
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(mountPoint.stMountPoint, 'map')">Bing Street</a>
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getAmapUrl(mountPoint.stMountPoint, 'map')">Amap Street & Satellite</a>
                        <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBaiduUrl(mountPoint.stMountPoint, 'map')">Baidu Street & Satellite</a>
                      </div>
                    </div>
                  </div>
                </ng-template>
              </div>

              <!-- Legacy duplicate details block removed -->

              <!-- RTK Base Analysis Report subsection（标题与操作始终显示，内容按需显示） -->
              <ng-template #analysisSection>
              <div class="row mt-2 subsection" *ngIf="showAnalyzer">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                    <h4 class="m-0 subsection-title">RTK Base Analysis Report</h4>
                    <div class="d-flex gap-2 align-items-center">
                    
                    </div>
                  </div>
                  <!-- Controls moved from header into main body -->
                  <div class="d-flex align-items-center gap-2 mb-2 justify-content-end">
                    <div class="d-flex align-items-center me-2">
                      <label class="me-2 small mb-0">Analysis duration in seconds</label>
                      <select class="form-select form-select-sm" style="width: 100px;"
                              [ngModel]="analyzerDurationByMountpoint[mountPoint.stMountPoint] || 120"
                              (ngModelChange)="setAnalyzerDuration(mountPoint.stMountPoint, $event)">
                        <option [ngValue]="10">10</option>
                        <option [ngValue]="30">30</option>
                        <option [ngValue]="60">60</option>
                        <option [ngValue]="120">120</option>
                      </select>
                    </div>
                    <button *ngIf="!isAnalyzerPage" type="button" class="btn btn-sm btn-outline-secondary me-2" (click)="onGetAnalysisReport(mountPoint)">Info_From_Analyzer</button>
                    <button type="button" class="btn btn-sm btn-primary" [disabled]="!isMountpointActive(mountPoint.stMountPoint)" (click)="onStartAnalysis(mountPoint)">{{ getAnalyzeButtonLabel(mountPoint) }}</button>
                  </div>
                  <div class="btn-group mb-2 float-end" *ngIf="analysisJsonByMountpoint[mountPoint.stMountPoint]">
                    <button class="btn btn-sm btn-outline-secondary" (click)="expandAll(mountPoint.stMountPoint)">Expand All</button>
                    <button class="btn btn-sm btn-outline-secondary" (click)="collapseAll(mountPoint.stMountPoint)">Collapse All</button>
                    <button class="btn btn-sm btn-outline-secondary" (click)="copyJson(mountPoint.stMountPoint)">Copy JSON</button>
                  </div>
                  <div class="json-tree" *ngIf="analysisJsonByMountpoint[mountPoint.stMountPoint] as result">
                    <ng-template #renderNode let-key="key" let-value="value" let-path="path" let-mp="mp">
                      <div class="json-row" *ngIf="!isObject(value) && !isArray(value)">
                        <span class="json-key">{{ key }}:</span>
                        <a *ngIf="isUrl(value)" class="json-value" [href]="value" target="_blank" rel="noopener">{{ value }}</a>
                        <span *ngIf="!isUrl(value)" class="json-value">{{ value }}</span>
                      </div>
                      <div class="json-node" *ngIf="isObject(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <div class="json-children" *ngIf="isExpanded(mp, path)">
                          <ng-container *ngFor="let child of value | keyvalue:keyOrder">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: child.key, value: child.value, path: path + '.' + child.key, mp: mp }"></ng-container>
                          </ng-container>
                        </div>
                      </div>
                      <div class="json-array" *ngIf="isArray(value)">
                        <div class="json-key clickable" (click)="toggleNode(mp, path)">
                          {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                        </div>
                        <ul class="json-ul" *ngIf="isExpanded(mp, path)">
                          <li *ngFor="let v of value; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: path + '[' + idx + ']', mp: mp }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>

                    <ng-container *ngIf="isObject(result); else renderRootArray">
                      <ng-container *ngFor="let item of result | keyvalue:keyOrder">
                        <ng-container *ngTemplateOutlet="renderNode; context: { key: item.key, value: item.value, path: item.key, mp: mountPoint.stMountPoint }"></ng-container>
                      </ng-container>
                    </ng-container>
                    <ng-template #renderRootArray>
                      <div class="json-array">
                        <ul class="json-ul">
                          <li *ngFor="let v of result; let idx = index">
                            <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: '[' + idx + ']', mp: mountPoint.stMountPoint }"></ng-container>
                          </li>
                        </ul>
                      </div>
                    </ng-template>
                  </div>
                </div>
              </div>
              </ng-template>
              <!-- Downloader subsection -->
              <ng-template #downloaderSection>
              <div class="row mt-2 subsection" *ngIf="showDownloader">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                    <h4 class="m-0 subsection-title">Record RTK Base Data from this Mountpoint.</h4>
                    <div></div>
                  </div>
                </div>
                <div class="col-12 d-flex justify-content-between align-items-end flex-wrap">
                  <div class="row g-2 flex-grow-1 w-100">
                    <div class="col-md-5 order-1">
                      <label class="form-label small">Recording Start Time</label>
                      <input type="datetime-local" class="form-control form-control-sm"
                             [ngModel]="getPlanStartAtInput(mountPoint.stMountPoint)"
                             (ngModelChange)="onPlanStartAtInputChange(mountPoint.stMountPoint, $event)"
                             (keydown)="$event.preventDefault()" (keypress)="$event.preventDefault()" (paste)="$event.preventDefault()"
                             [attr.inputmode]="'none'">
                    </div>
                    <div class="col-md-4 order-2">
                      <label class="form-label small">Recording Duration</label>
                      <select class="form-select form-select-sm"
                              [ngModel]="getDurationSelection(mountPoint.stMountPoint)"
                              (ngModelChange)="onDurationSelectionChange(mountPoint.stMountPoint, $event)">
                        <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                      </select>
                    </div>
                    <!-- Preload time removed; hard-coded to 1 minute in submit logic -->
                  </div>
                  <div class="ms-auto mt-2 mt-md-0"></div>
                </div>
                <!-- Buttons row: left fetch URL, right submit task -->
                <div class="col-12 mt-2 d-flex align-items-center">
                  <div>
                    <button type="button" class="btn btn-sm btn-primary" (click)="onStartDownloader(mountPoint)"
                            [disabled]="downloaderLoading[mountPoint.stMountPoint] || (downloaderUrlByMountpoint[mountPoint.stMountPoint]?.running) || isDownloaderStartTimeRejected(mountPoint.stMountPoint) || !isDownloaderStartTimeValid(mountPoint.stMountPoint)">
                      Submit the Recording Task
                    </button>
                  </div>
                  <div class="ms-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                            (click)="onGetDownloaderUrl(mountPoint)">
                      Refresh status
                    </button>
                  </div>
                </div>
                <!-- Professional file item (always visible) -->
                <div class="col-12 mt-2">
                  <div class="mb-1 fw-semibold">Recorded RTKBase RTCM File:</div>
                  <div class="d-flex align-items-center justify-content-between gap-3 border rounded p-2 bg-light">
                    <ng-container *ngIf="downloaderUrlByMountpoint[mountPoint.stMountPoint] as dl; else dlEmpty">
                      <ng-container *ngIf="!dl.running; else runningTpl">
                        <ng-container *ngIf="dl.url; else noFileTpl">
                          <div class="w-100 me-3">
                            <div class="d-flex align-items-center justify-content-between gap-3">
                              <a class="fw-semibold link-primary"
                                 [href]="isExpired(dl) ? null : dl.url"
                                 [class.disabled]="isExpired(dl)"
                                 [attr.aria-disabled]="isExpired(dl) ? true : null"
                                 (click)="isExpired(dl) ? $event.preventDefault() : null"
                                 target="_blank" rel="noopener">
                                rtcm_data.rtcm3
                              </a>
                              <div class="text-muted fw-semibold flex-shrink-0">
                                {{ dl.lastModified ? (dl.lastModified | date:'medium') : ('--') }}
                              </div>
                            </div>
                            <ng-template #expiredTpl>
                              <div class="text-primary small mt-1">(This file has expired and is no longer available for download.)</div>
                            </ng-template>
                            <div *ngIf="isExpired(dl)">
                              <ng-container [ngTemplateOutlet]="expiredTpl"></ng-container>
                            </div>
                          </div>
                          <div>
                            <a class="btn btn-sm btn-outline-primary"
                               [href]="isExpired(dl) ? null : dl.url"
                               [class.disabled]="isExpired(dl)"
                               [attr.aria-disabled]="isExpired(dl) ? true : null"
                               (click)="isExpired(dl) ? $event.preventDefault() : null"
                               target="_blank" rel="noopener" download
                               aria-label="Download">
                              <span class="material-icons" style="font-size: 1.1rem;" aria-hidden="true">download</span>
                            </a>
                          </div>
                        </ng-container>
                        <ng-template #noFileTpl>
                          <div class="text-muted">No recorded file available for download</div>
                        </ng-template>
                      </ng-container>
                      <ng-template #runningTpl>
                        <div class="text-primary">A recorder is currently running or scheduled to be run. Please check back later</div>
                      </ng-template>
                    </ng-container>
                    <ng-template #dlEmpty>
                      <div class="text-muted">No recorded file available for download</div>
                    </ng-template>
                  </div>
                </div>
              </div>
              </ng-template>


            </div>
          </div>
        
      </div>  
    
      <!-- Horizontal Divider -->
      <div class="row">
        <div class="col-12">
          <br>
        </div>
      </div>
    
      <!-- Analysis JSON 展示区 -->
      <div class="row mt-2" *ngIf="analysisJson">
        <div class="col-12">
          <h3>Analysis Result for {{ analysisJsonMountpoint }}</h3>
          <pre class="p-3 bg-light border rounded" style="white-space: pre-wrap; word-break: break-word;">
{{ analysisJson | json }}
          </pre>
        </div>
      </div>

      <!-- 已移除：底部单独的编辑表单（已整合到每个列表项内） -->
    </div>
</div>

</ng-template>

</ng-template>




  

